import { API, Base, CSS_VAR_RULE_MAP, ComponentJSON } from '@jds/layers-parser';
import { cloneDeep, get } from '@jds/utils';

import { CustomJSON } from './inputJsonTypes';
import { FlatTokens } from './interfaces';

export interface Hierarchy {
  [key: string]: Array<string | Hierarchy>;
}

type StringMap = Record<string, string>;
export type Value = string | number | boolean | StringMap;
export type LayerMap = Record<string, Value>;

export interface Combination {
  [key: string]: Combination | Base;
}

export type Combinations = Array<Combination>;

export type Template = string;
export type Style = string;
export type TemplateMap = Record<string, Template>;

const EVENTS_MAP: Record<string, string | string[]> = {
  onClick: 'onClick',
  onFocus: 'onFocus',
  onBlur: 'onBlur',
  onHover: 'onMouseEnter',
  onChange: 'onChange',
  onHoverEnd: 'onMouseLeave',
  onKeydown: 'onKeyDown',
  onKeyup: 'onKeyUp',
  onInvalid: 'onInvalid',
  onLoad: 'onLoad',
  onError: 'onError',
  onPaste: 'onPaste',
  onScroll: 'onScroll',
  onSwipeLeft: [
    'onPointerDown',
    'onPointerMove',
    'onPointerUp',
    'onPointerOut',
    'onDragStart',
    'onDragEnd'
  ]
};

const filterInvalidValue = (v: unknown) => {
  if (typeof v === 'undefined') return false;
  if (v === '') return false;
  if (v === null) return false;
  return true;
};

function getEventName(key: string): string | string[] {
  const match = Object.keys(EVENTS_MAP).find((evt) => key === evt);
  const matchedEvent = EVENTS_MAP[match];
  return matchedEvent;
}

export const getComponentConfig = (
  configAPI: API['config'],
  values: { [x: string]: any }
) => {
  const configVal: Record<string, any> = {};
  Object.keys(configAPI).forEach((conf) => {
    if (conf in values) {
      configVal[conf] = values[conf];
    } else {
      configVal[conf] = configAPI[conf].values.pop();
    }
  });
  return configVal;
};

export const internalLayerPrefixes = ['jds', 'tooltip'];

export const processConfigAPI = (
  config: ComponentJSON['api']['config'],
  values: Record<string, unknown>,
  layerProps?: Base
) => {
  if (!values) return {};
  const finalData: any = {};
  Object.entries(config || {}).forEach(([name, item]) => {
    const resolvedConfigValues = item.values || [];
    if (!layerProps?.[name]) {
      if (!resolvedConfigValues.includes(values[name] as string)) {
        if (filterInvalidValue(resolvedConfigValues[0]))
          finalData[name] = resolvedConfigValues[0];
      } else {
        if (filterInvalidValue(values[name])) finalData[name] = values[name];
      }
    }
  });
  return finalData;
};

const acceptedInternalProps = [
  'internalIndex',
  'layerRefs',
  'className',
  'internalClasses'
];

export const processDataAPI = (
  api: ComponentJSON['api'],
  currentLayer: string,
  values: Record<string, unknown>
) => {
  if (!currentLayer) return {};
  const processData: Record<string, unknown> = {};
  const processedEvents: Record<string, (props: any) => void> = {};
  let processedA11y: Record<string, unknown> = {};

  const { data = {}, events, children = {} } = api;

  const currentDataItem = data[currentLayer];

  Object.entries(currentDataItem || {}).forEach(
    ([name, { name: propName = '' }]) => {
      const currentName = propName.slice(
        propName.lastIndexOf('.') + 1,
        propName.length
      );
      const value =
        get(values, propName, undefined) || get(values, currentName, undefined);

      if (typeof value !== 'undefined') processData[name] = value;
    }
  );

  if (events) {
    const currentEventItem = events[currentLayer];

    if (currentEventItem) {
      Object.entries(currentEventItem).forEach(([internalName, valueName]) => {
        const eventMapItem = getEventName(internalName);
        const resolvedName =
          typeof valueName === 'string' ? valueName : valueName.name;

        if (!Array.isArray(eventMapItem)) {
          processedEvents[eventMapItem || internalName] =
            values[resolvedName] || (values[eventMapItem] as any);
        } else {
          eventMapItem.forEach((item) => {
            processedEvents[item] = values[item] as any;
          });
          processedEvents[internalName] = values[resolvedName] as any;
        }
      });
    }
  }
  if (api.a11y) {
    processedA11y = processA11yData(api.a11y, currentLayer, values);
  }

  acceptedInternalProps.map((item) => {
    if (item in values) processData[item] = values[item];
  });

  return {
    processData,
    processedEvents,
    processedA11y,
    processedChildren: children
  };
};
const processA11yData = (
  data: API['a11y'],
  layer: string,
  api: Record<string, unknown>
) => {
  if (!data[layer]) return {};
  const { properties } = data[layer];
  if (!properties) return {};
  const processed: Record<string, unknown> = {};
  Object.entries(properties).forEach(([internalProperty, property]) => {
    if (typeof property === 'string') {
      const normalisedProperty = property.slice(
        property.lastIndexOf('.') + 1,
        property.length
      );
      processed[internalProperty] = api[normalisedProperty];
    } else {
      const { name, value, prop } = property;
      const normalisedName = name?.slice(
        name.lastIndexOf('.') + 1,
        name.length
      );
      const defaultValue = api[prop || ''] || value;
      processed[internalProperty] = api[normalisedName] || defaultValue;
    }
  });
  return processed;
};

const validateProp = (prop: unknown) => {
  if (typeof prop === 'undefined') return false;
  if (prop === null) return false;
  if (prop === '') return false;
  return true;
};

export const mergeStyleProperties = (
  props: Record<string, unknown>,
  uiTokens: Base,
  data: ComponentJSON['api']['data'],
  layerProps: Base
) => {
  const modifiedStyles: Base = cloneDeep(uiTokens);
  Object.entries(data || {}).forEach(([layer, properties]) => {
    Object.entries(properties).forEach(([internalName, { name }]) => {
      modifiedStyles[layer] = modifiedStyles[layer] || {};
      if (validateProp(props[name])) {
        if (name in CSS_VAR_RULE_MAP) {
          if (!layerProps[layer] || !(name in layerProps[layer])) {
            modifiedStyles[layer][name] = props[name] as string | number;
          } else {
            delete modifiedStyles[layer][name];
          }
        } else {
          delete modifiedStyles[layer][name];
        }
        if (internalName in CSS_VAR_RULE_MAP) {
          if (!layerProps[layer] || !(internalName in layerProps[layer])) {
            modifiedStyles[layer][internalName] = props[name] as
              | string
              | number;
          } else {
            delete modifiedStyles[layer][internalName];
          }
        } else {
          delete modifiedStyles[layer][internalName];
        }
      }
    });
  });
  return modifiedStyles;
};

const allowedLayerProps = ['disabled', 'transition'];

export const seperateComponentLayerProps = (styles: Base = {}) => {
  const layerProps: Base = {};

  Object.entries(styles).forEach(([currentlayer, currentStyles]) => {
    const layerElements = currentlayer.split('-');
    const layerName = layerElements[layerElements.length - 1];

    if (!layerProps[currentlayer]) {
      layerProps[currentlayer] = {};
    }

    const isInternalLayer = internalLayerPrefixes.find((i) =>
      layerName.startsWith(i)
    );

    if (isInternalLayer) {
      Object.entries(currentStyles || {}).forEach(([property, value]) => {
        layerProps[currentlayer][property] = value;
      });
    }

    if (styles.value) {
      layerProps[currentlayer].value = currentStyles.value;
    }

    Object.entries(currentStyles).forEach(([property, value]) => {
      if (!(property in CSS_VAR_RULE_MAP)) {
        if (!(currentlayer in layerProps)) {
          layerProps[currentlayer] = {};
        }
        layerProps[currentlayer][property] = value;
      } else {
        if (allowedLayerProps.includes(property)) {
          layerProps[currentlayer][property] = value;
        }
      }
    });
  });
  return { layerProps, styles };
};

export const generateDefaultProperties = <T extends Record<string, string>>(
  json: ComponentJSON
) => {
  const finalObj: T = {} as T;
  try {
    Object.entries(json.api.config || {}).forEach(([key, item]) => {
      const resolvedConfigValues = item.values;
      finalObj[key as keyof T] = resolvedConfigValues[0] as T[keyof T];
    });
  } catch (error) {
    console.log(error);
  }

  return finalObj;
};

export const getLayerName = (layer: string) => {
  const layerElements = layer.split('-');
  const layerName = layerElements[layerElements.length - 1];

  if (layerName.startsWith('jds')) {
    const normalisedKey = layerName.replace('jds_', '');

    return normalisedKey;
  }
  return layerName;
};

export const tokenKeys = ['mode', 'theme'];

/**
 * Returns the root component name from a hierarchy object.
 *
 * @param hierarchy - The hierarchy object to find the root component name from.
 * @returns The root component name, or an empty string if not found.
 */
export function getRoot(hierarchy: ComponentJSON['hierarchy']): string {
  const root = hierarchy;
  if (typeof root === 'object') {
    return Object.keys(root)[0];
  }
  return '';
}

function getSubLayers(
  hierarchy: Hierarchy,
  layer: string
): Array<string> | undefined {
  const layerHierarchy = findHierarchy(hierarchy, layer);
  return layerHierarchy
    ? layerHierarchy[layer]?.flatMap((subLayer) => {
        if (typeof subLayer === 'string') {
          return subLayer;
        }
        const root = getRoot(subLayer);
        return [root].concat(getSubLayers(subLayer, root) ?? []);
      }) ?? undefined
    : undefined;
}

function findHierarchy(hierarchy: Hierarchy, layer: string): Hierarchy | null {
  if (hierarchy[layer]) {
    return hierarchy;
  }
  const root = getRoot(hierarchy);
  const subLayers = hierarchy[root];
  for (const subLayer of subLayers ?? []) {
    if (typeof subLayer === 'string') {
      if (subLayer === layer) {
        return null;
      }
    } else {
      const subHierarchy = findHierarchy(subLayer, layer);
      if (subHierarchy) {
        return subHierarchy;
      }
    }
  }
  return null;
}

export const updateLayersWithParentTokens = (
  tokens: FlatTokens,
  styles: Base,
  hierarchy: Hierarchy
) => {
  const modifiedTokens = { ...tokens };
  const modifiedBase = { ...styles };

  for (const key in styles) {
    const styleKeys = Object.keys(styles[key]);
    for (const styleKey of styleKeys) {
      if (tokenKeys.includes(styleKey)) {
        modifiedTokens[styleKey] = styles[key][styleKey] as string;
        const children = getSubLayers(hierarchy, key);
        if (children) {
          children.forEach((child) => {
            if (child in styles)
              modifiedBase[child][styleKey] = styles[key]?.[styleKey];
          });
        }
      }
    }
  }
  return { modifiedBase, modifiedTokens };
};

const BufferConverter = (value: string) => {
  return Buffer.from(value, 'base64').toString('utf-8');
};

export const convertBase64ToObject = <P extends object>(value?: string): P => {
  if (!value) return {} as P;
  const converter =
    typeof window === 'undefined' ? BufferConverter : window.atob;
  const objectString = converter(value);

  return JSON.parse(objectString || '{}');
};

export const convertToBase64 = (value: object) => {
  return window.btoa(JSON.stringify(value));
};

export const getCustomJSON = (custom?: string) => {
  const base64 = (custom?.split('--') || [])[1];
  const convertedCustom = convertBase64ToObject<CustomJSON>(base64);
  return convertedCustom;
};

export const getBaseLayerName = (layer = '') => {
  const parts = layer.split('-');
  return parts[parts.length - 1];
};

export const STYLE_ALLOWED_COMPONENTS = ['JDSContainer'];

export const clamp = (value: number, min: number, max: number) =>
  Math.max(min, Math.min(value, max));
